import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedRelative } from 'react-intl';

// components
import PdpSharedSidebarPriceAmount from './PdpSharedSidebarPriceAmount/PdpSharedSidebarPriceAmount';

// helpers
import { getAmountByCurrency } from 'dibs-selectors/dist/amount';
import { calculatePercentOff } from '../helpers/price';

/**
 * Price Components for an item on sale
 *  (Sale or Sale & Original)
 */
export function SaleItemPrices({ showOriginal, currency, pricing, quantityDisplay }) {
    const { salePrice, originalPrice } = pricing;
    const saleAmount = getAmountByCurrency(salePrice)(currency);
    const originalAmount = showOriginal && getAmountByCurrency(originalPrice)(currency);

    let discount;
    if (originalAmount && saleAmount) {
        discount = calculatePercentOff({ reducedAmount: saleAmount, originalAmount });
    }

    return (
        <div>
            {/* ORIGINAL PRICE */}
            {!!originalAmount && (
                <PdpSharedSidebarPriceAmount
                    hasSalePrice
                    discount={discount}
                    currency={currency}
                    amount={originalAmount}
                    quantityDisplay={quantityDisplay}
                />
            )}
            {/* SALE PRICE */}
            <PdpSharedSidebarPriceAmount
                type="SALE"
                currency={currency}
                amount={saleAmount}
                quantityDisplay={quantityDisplay}
            />
        </div>
    );
}

SaleItemPrices.propTypes = {
    showOriginal: PropTypes.bool.isRequired,
    currency: PropTypes.string,
    quantityDisplay: PropTypes.string,
    pricing: PropTypes.shape({
        originalPrice: PropTypes.object,
        salePrice: PropTypes.object.isRequired,
    }).isRequired,
};

/**
 * Price Components for an item with a net price
 *  (Net or Net & Retail)
 */
export function NetItemPrices({ showRetail, currency, pricing, quantityDisplay }) {
    const { netPrice, retailPrice } = pricing;
    const netAmount = getAmountByCurrency(netPrice)(currency);
    const retailAmount = showRetail && getAmountByCurrency(retailPrice)(currency);

    let discount;
    if (retailAmount && netAmount) {
        discount = calculatePercentOff({ reducedAmount: netAmount, originalAmount: retailAmount });
    }

    return (
        <div>
            {/* RETAIL PRICE */}
            {!!retailAmount && (
                <PdpSharedSidebarPriceAmount
                    showLabel
                    hasNetPrice
                    discount={discount}
                    currency={currency}
                    amount={retailAmount}
                    quantityDisplay={quantityDisplay}
                />
            )}
            {/* NET PRICE */}
            {!!netAmount && (
                <PdpSharedSidebarPriceAmount
                    type="NET"
                    showLabel
                    amount={netAmount}
                    currency={currency}
                    quantityDisplay={quantityDisplay}
                />
            )}
        </div>
    );
}

NetItemPrices.propTypes = {
    showRetail: PropTypes.bool.isRequired,
    currency: PropTypes.string,
    quantityDisplay: PropTypes.string,
    pricing: PropTypes.shape({
        retailPrice: PropTypes.object,
        netPrice: PropTypes.object.isRequired,
    }).isRequired,
};

/**
 * Price Components for an item with just a retail price
 */
export function RetailItemPrices({ currency, pricing, showListPriceLabel, quantityDisplay }) {
    const { retailPrice } = pricing;
    const retailAmount = getAmountByCurrency(retailPrice)(currency);

    return (
        <div>
            {/* RETAIL PRICE */}
            <PdpSharedSidebarPriceAmount
                currency={currency}
                amount={retailAmount}
                showLabel={showListPriceLabel}
                quantityDisplay={quantityDisplay}
            />
        </div>
    );
}

RetailItemPrices.propTypes = {
    currency: PropTypes.string,
    showListPriceLabel: PropTypes.bool,
    quantityDisplay: PropTypes.string,
    pricing: PropTypes.shape({
        retailPrice: PropTypes.object.isRequired,
    }).isRequired,
};

export function PrivateOfferItemPrices({
    currency,
    amount,
    expiration,
    showExpirationDate,
    pricing,
    quantityDisplay,
    showRetail,
    showNet,
}) {
    const { netPrice, retailPrice } = pricing;
    const netAmount = showNet && getAmountByCurrency(netPrice)(currency);
    const retailAmount = showRetail && getAmountByCurrency(retailPrice)(currency);

    const formattedExpiration = showExpirationDate && <FormattedRelative value={expiration} />;
    const expirationNode = showExpirationDate && (
        <FormattedMessage
            id="pdp.SharedPdpItemPrices.privateOfferExpires"
            defaultMessage="Expires {expiration}"
            values={{ expiration: formattedExpiration }}
        />
    );

    return (
        <div>
            {!!retailAmount && (
                <PdpSharedSidebarPriceAmount
                    showLabel
                    hasPrivateOffer
                    currency={currency}
                    amount={retailAmount}
                    quantityDisplay={quantityDisplay}
                />
            )}
            {!!netAmount && (
                <PdpSharedSidebarPriceAmount
                    type="NET"
                    showLabel
                    hasPrivateOffer
                    amount={netAmount}
                    currency={currency}
                    quantityDisplay={quantityDisplay}
                />
            )}
            <PdpSharedSidebarPriceAmount
                type="PRIVATE_OFFER"
                currency={currency}
                amount={amount}
                expiration={expirationNode}
                showLabel
                quantityDisplay={quantityDisplay}
            />
        </div>
    );
}

PrivateOfferItemPrices.propTypes = {
    currency: PropTypes.string,
    amount: PropTypes.number,
    showListPriceLabel: PropTypes.bool,
    showExpirationDate: PropTypes.bool,
    showNet: PropTypes.bool,
    showRetail: PropTypes.bool,
    expiration: PropTypes.string,
    pricing: PropTypes.object,
    quantityDisplay: PropTypes.string,
};
